import { TextBadge } from '@carvana/ds-badge';
import { TaxCreditCvna } from '@carvana/ds-icons';
import React, { useCallback, useState } from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import { useBrandCapabilities } from '|/brand';
import Loading from '|/components/base/loading';
import NoDealerFeesModal from '|/components/carvana-ds/no-dealer-fees-modal';
import {
  OrderSummaryDisplay,
  GettingVehicle,
  HowYourePaying,
  PriceBreakdown,
  TradeInModal,
  DealerMarketPlaceReminder
} from '|/components/carvana-ds/order-summary-display';
import CarvanaCareModal from '|/components/carvana-ds/order-summary-display/carvana-care-modal';
import VehicleImage from '|/components/vehicle-image';
import { isNewVehicle } from '|/helpers';
import { getDealerFeeData, getDealerFeeAmount } from '|/helpers/dealer-fees';
import { useIsInBucket } from '|/helpers/experiment';

import { useIsMobileViewport } from '|/hooks/use-is-mobile-viewport';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import styles from './order-summary.module.css';

interface OrderSummaryProps {
  summary: ReviewSummaryResponse;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const OrderSummary = ({ summary, isOpen, setIsOpen }: OrderSummaryProps) => {
  const {
    vehicle: {
      year = null,
      make = null,
      model = null,
      odometer = 0,
      trim = null,
      stockNumber = null,
      isEvTaxCreditEligible = false,
      offering = null
    } = {}
  } = summary || {};
  const [isTradeInModalOpen, setIsTradeInModalOpen] = useState(false);
  const [isDealerFeesModalOpen, setIsDealerFeesModalOpen] = useState(false);
  const [isCarvanaCareModalOpen, setIsCarvanaCareModalOpen] = useState(false);
  const isMobileViewport = useIsMobileViewport();
  const { checkout } = useBrandCapabilities();

  // Check if user is in the pp-no-dealer-fees experiment
  // This hook call triggers the bucketing event when the component mounts
  const isInNoDealerFeesExperiment = useIsInBucket('pp-no-dealer-fees', 'treatment', ['control']);

  const formatMileage = useCallback((value: number) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0
    }).format(Math.abs(value));
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsTradeInModalOpen(false);
    setIsDealerFeesModalOpen(false);
  }, [setIsOpen, setIsTradeInModalOpen]);

  // Show loading state if experiment bucket is still being determined
  if (isInNoDealerFeesExperiment === null) return <Loading />;

  // Calculate dealer fee metadata for all users
  let eventMetadata;
  const userState = summary?.customer?.address?.state;
  if (userState) {
    const stateData = getDealerFeeData(userState);
    if (stateData) {
      eventMetadata = {
        feeBasedState: stateData.stateCode,
        maxDealerFeeDisplayed: getDealerFeeAmount(stateData)
      };
    } else {
      eventMetadata = { feeBasedState: userState, maxDealerFeeDisplayed: null };
    }
  } else {
    eventMetadata = { feeBasedState: null, maxDealerFeeDisplayed: null };
  }

  const modalStyleProps = isMobileViewport
    ? ({ mobileStyle: 'full' } as const)
    : ({ drawerStyle: 'all', drawerSide: 'right', drawerWidth: '400px' } as const);

  return (
    <EventedModal
      analyticsId={ModalNames.OrderSummary}
      isOpen={isOpen}
      onClose={handleClose}
      modalHeader="Order Summary"
      {...modalStyleProps}
      contentPadding={false}
      disableOutsideClose={isTradeInModalOpen || isDealerFeesModalOpen || isCarvanaCareModalOpen}
      divLine={false}
      eventMetadata={eventMetadata}
    >
      <div className={styles.vehicleDetails}>
        <div className={styles.vehicleImage}>
          <VehicleImage stockNumber={stockNumber} width={200} alt="" data-testid="vehicle-image" />
        </div>
        <div className={styles.vehicleContent}>
          <div className={styles.badges}>
            {checkout.sellsPreOwnedVehicles && isNewVehicle(summary?.vehicle) && (
              <TextBadge customBgColor="var(--decorative-3-strong)" priority="custom">
                Brand new
              </TextBadge>
            )}
            {isEvTaxCreditEligible && (
              <div className={styles.taxCreditBadge} data-testid="tax-credit-badge">
                <TaxCreditCvna size={16} />
                <span>Tax Credit</span>
              </div>
            )}
          </div>
          <div className={styles.vehicleInfo}>
            <h2 className={styles.vehicleTitle}>{[year, make, model].filter(Boolean).join(' ')}</h2>
            <p>{trim}</p>
            <p>{formatMileage(odometer)} miles</p>
          </div>
        </div>
      </div>

      <OrderSummaryDisplay
        variant="sidebar"
        isInNoDealerFeesExperiment={isInNoDealerFeesExperiment}
      >
        <GettingVehicle summary={summary} variant="sidebar" vehicleType={offering ?? undefined} />

        <PriceBreakdown
          summary={summary}
          vinEtchingStatus={null}
          setIsTradeInModalOpen={setIsTradeInModalOpen}
          setIsCarvanaCareModalOpen={setIsCarvanaCareModalOpen}
          setIsDealerFeesModalOpen={setIsDealerFeesModalOpen}
          isInNoDealerFeesExperiment={isInNoDealerFeesExperiment}
        />

        <HowYourePaying summary={summary} />

        <DealerMarketPlaceReminder summary={summary} />

        <TradeInModal
          summary={summary}
          isOpen={isTradeInModalOpen}
          setIsOpen={setIsTradeInModalOpen}
        />

        <CarvanaCareModal
          isOpen={isCarvanaCareModalOpen}
          setIsOpen={setIsCarvanaCareModalOpen}
          coverageType={
            (summary?.product?.type as 'Essential' | 'Cosmetic' | 'Plus' | 'Premier') || 'Essential'
          }
          coverageDetails={{
            duration: `${summary?.product?.months} months`,
            mileage: `+${summary?.product?.mileage?.toLocaleString()} miles`
          }}
          fuelDescription={summary?.vehicle?.fuelDescription}
        />

        {isInNoDealerFeesExperiment && (
          <NoDealerFeesModal
            isOpen={isDealerFeesModalOpen}
            onClose={() => {
              setIsDealerFeesModalOpen(false);
            }}
            userState={userState}
          />
        )}
      </OrderSummaryDisplay>
    </EventedModal>
  );
};

export default OrderSummary;
